import '../../../../../theme/partials/_amount.scss';

import { App } from '@capacitor/app';
import {
  IonCol,
  IonGrid,
  IonIcon,
  IonInfiniteScroll,
  IonLabel,
  IonList,
  IonRefresher,
  IonRefresherContent,
  IonRow,
  IonSpinner,
  isPlatform,
} from '@ionic/react';
import classNames from 'classnames';
import { chevronForwardOutline } from 'ionicons/icons';
import {
  ReactNode, useEffect, useRef, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';

import DashboardEmpty from '@/assets/imgs/dashboard-empty.svg';
import { OPERATION_DASHBOARD_DETAIL } from '@/constants';
import useMediaQuery, { Breakpoint } from '@/hooks/useMediaQuery';
import { DashboardColumns } from '@/interfaces/DashboardInterface/DashboardInterface';
import { saveOperationData } from '@/redux/operation/operations';
import {
  Dashboard,
} from '@/redux/operation/operationsInterface';
import { storageService } from '@/services/storage';
import { getCustomerFullName } from '@/utils/customerHelper';
import { getDefaultDashboardColumns } from '@/utils/dashboardColumnts';

import styles from './DashboardTableMobile.module.scss';

interface DashboardTableMobileProps {
  data: any;
  onChangeShowAGCode: (showAgCode: boolean) => void;
  loadingData: boolean;
  isRefreshing: boolean;
  onScrollEnd: (event?: CustomEvent) => void;
  moreItems: boolean;
  emptyMessage?: ReactNode;
  emptyMessageId?: string;
}

export default function DashboardTableMobile({
  data,
  onChangeShowAGCode,
  loadingData,
  isRefreshing,
  onScrollEnd,
  moreItems,
  emptyMessage,
  emptyMessageId,
}: DashboardTableMobileProps) {
  const history = useHistory();
  const dispatch = useDispatch();

  const infiniteTarget = useRef<HTMLIonInfiniteScrollElement>(null);

  const isTablet = useMediaQuery(Breakpoint.Tablet);
  const isDesktop = useMediaQuery(Breakpoint.Desktop);
  const isDesktopLarge = useMediaQuery(Breakpoint.DesktopLarge);

  const [cookieProposalReference, setCookieProposalReference] = useState(storageService.hasAGCode());
  const [appVersion, setAppVersion] = useState('');
  const [isMobile, setIsMobile] = useState(!isTablet && !isDesktop);
  const [dashboardColumns, setDashboardColumns] = useState<DashboardColumns[]>([]);
  const customDashboardColumns = [
    DashboardColumns.STATUS,
    DashboardColumns.DNI_CIF,
    DashboardColumns.CUSTOMER,
    DashboardColumns.DATE,
    DashboardColumns.AMOUNT,
  ];

  if (!isPlatform('mobileweb') && !isPlatform('desktop')) {
    App?.getInfo().then((version) => {
      setAppVersion(version.version);
    });
  }

  useEffect(() => {
    const newValue = storageService.hasAGCode();
    if (cookieProposalReference !== newValue) {
      setCookieProposalReference(newValue);
    }
  }, [storageService.hasAGCode()]);

  useEffect(() => {
    if (cookieProposalReference) customDashboardColumns.splice(2, 0, DashboardColumns.AG_CODE);
    if ((isDesktop && customDashboardColumns.includes(DashboardColumns.AG_CODE)) || cookieProposalReference) {
      onChangeShowAGCode(true);
      storageService.setAGCode(true);
    } else {
      onChangeShowAGCode(false);
      storageService.setAGCode(false);
    }
  }, [isDesktop, customDashboardColumns, cookieProposalReference]);

  useEffect(() => {
    setIsMobile(!isTablet && !isDesktop);
    if (isDesktopLarge) {
      setDashboardColumns(customDashboardColumns);
    } else {
      setDashboardColumns(getDefaultDashboardColumns());
    }
  }, [isTablet, isDesktop, isDesktopLarge]);

  useEffect(() => {
    if (!loadingData) {
      infiniteTarget.current?.complete();
    }
  }, [loadingData]);

  const padNumber = (num: number): string => (num < 10 ? `0${num}` : `${num}`);

  const convertDateFormat = (inputDate: string): string => {
    const date = new Date(inputDate);
    return `${padNumber(date.getDate())}/${padNumber(
      date.getMonth() + 1,
    )}/${date.getFullYear()}`;
  };

  const handleClick = (value: any) => {
    const operationSelected = {
      data: value,
    };
    dispatch(saveOperationData(operationSelected));
    if (value.order) {
      history.push(`${OPERATION_DASHBOARD_DETAIL}/?cart_id=${value.order.cart_id}&order_id=${value.order.order_id}`);
    } else {
      history.push(`${OPERATION_DASHBOARD_DETAIL}/?credit_proposal_id=${value.proposal.credit_proposal_id}`);
    }
    // history.push(OPERATION_DASHBOARD_DETAIL);
  };

  const renderColumnData = (column: DashboardColumns, rowData: Dashboard) => {
    const {
      business_status_description,
      customer,
      created_date,
      order,
      proposal,
      application,
      product,
    } = rowData;

    switch (column) {
      case DashboardColumns.STATUS:
        return (
          <IonCol key={column} className={classNames(styles.statusCol, styles.dashboardInitialRow, isMobile || !isDesktopLarge ? styles.mobile : '')}>
            <IonLabel className={classNames(styles.targetStatusLabel, business_status_description)}>
              {business_status_description}
            </IonLabel>
            {(isMobile || !isDesktopLarge) && dashboardColumns.includes(DashboardColumns.AMOUNT) ? (
              <div>
                <IonLabel className={classNames(styles.tableAmount, styles.tableAmountMobile, business_status_description ? `amount-${business_status_description}` : '')}>
                  {order?.total_price?.toLocaleString('de-DE', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }) || proposal?.principal_amount?.toLocaleString('de-DE', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                  €
                </IonLabel>
                <IonIcon ios={chevronForwardOutline} className={styles.targetStatusIcon} />
              </div>
            ) : (
              <IonIcon ios={chevronForwardOutline} className={styles.targetStatusIcon} />
            )}
          </IonCol>
        );

      case DashboardColumns.DNI_CIF:
        return (
          <IonCol key={column} className={styles.documentId}>
            <IonLabel className={styles.columnTruncate}>{customer?.document_id}</IonLabel>
          </IonCol>
        );

      case DashboardColumns.AG_CODE:
        return cookieProposalReference && (isDesktop || isDesktopLarge) ? (
          <IonCol key={column}>
            <IonLabel className={classNames(styles.proposalReferenceContent, styles.columnTruncate)}>
              {proposal?.proposal_reference && proposal.proposal_reference}
            </IonLabel>
          </IonCol>
        ) : null;

      case DashboardColumns.CUSTOMER:
        if ((isMobile || !isDesktopLarge) || (!isMobile && isDesktopLarge)) {
          return (
            <IonCol key={column} className={classNames(styles.nameSurname, isMobile || !isDesktopLarge ? styles.mobile : styles.clientName)}>
              <IonLabel className={styles.columnTruncate}>
                {getCustomerFullName(customer).toLocaleLowerCase()}
              </IonLabel>
            </IonCol>
          );
        }
        return null;

      case DashboardColumns.DATE:
        return (
          <IonCol key={column} className={styles.operationMobileRow}>
            <IonLabel className={classNames(styles.createdDate, styles.columnTruncate)}>
              {convertDateFormat(created_date)}
            </IonLabel>
          </IonCol>
        );

      case DashboardColumns.AMOUNT:
        if (!isMobile && isDesktopLarge) {
          return (
            <IonCol key={column}>
              <IonLabel className={classNames(styles.tableAmount, styles.columnTruncate, business_status_description ? `amount-${business_status_description}` : '')}>
                {order?.total_price?.toLocaleString('de-DE', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }) || proposal?.principal_amount?.toLocaleString('de-DE', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
                {' '}
                €
              </IonLabel>
            </IonCol>
          );
        }
        return null;

      case DashboardColumns.EMAIL:
        return (
          <IonCol key={column} className={styles.operationMobileRow}>
            <IonLabel className={classNames(styles.createdDate, styles.columnTruncate)}>
              {customer?.email}
            </IonLabel>
          </IonCol>
        );

      case DashboardColumns.OFFER:
        return (
          <IonCol key={column} className={styles.operationMobileRow}>
            <IonLabel className={classNames(styles.createdDate, styles.columnTruncate)}>
              {proposal?.commercial_offer_name}
            </IonLabel>
          </IonCol>
        );

      case DashboardColumns.PROPOSAL_TERM:
        return (
          <IonCol key={column} className={styles.operationMobileRow}>
            <IonLabel className={classNames(styles.createdDate, styles.columnTruncate)}>
              {proposal?.term}
            </IonLabel>
          </IonCol>
        );
      case DashboardColumns.CART_ID:
        return (
          <IonCol key={column} className={styles.operationMobileRow}>
            <IonLabel className={classNames(styles.createdDate, styles.columnTruncate)}>
              {order?.cart_id}
            </IonLabel>
          </IonCol>
        );
      case DashboardColumns.PRODUCT:
        return (
          <IonCol key={column} className={styles.operationMobileRow}>
            <IonLabel className={classNames(styles.createdDate, styles.columnTruncate)}>
              {product}
            </IonLabel>
          </IonCol>
        );
      case DashboardColumns.APPLICATION:
        return (
          <IonCol key={column} className={styles.operationMobileRow}>
            <IonLabel className={classNames(styles.createdDate, styles.columnTruncate)}>
              {application}
            </IonLabel>
          </IonCol>
        );
      case DashboardColumns.PRODUCTS_NUM:
        return (
          <IonCol key={column} className={styles.operationMobileRow}>
            <IonLabel className={classNames(styles.createdDate, styles.columnTruncate)}>
              {order?.total_products}
            </IonLabel>
          </IonCol>
        );
      case DashboardColumns.PROCESS_TYPE:
        return (
          <IonCol key={column} className={styles.operationMobileRow}>
            <IonLabel className={classNames(styles.createdDate, styles.columnTruncate)}>
              {order?.process_type}
            </IonLabel>
          </IonCol>
        );

      default:
        return null;
    }
  };

  return (
    <div className={styles.dashboardMobileTableOuter}>
      <IonList className={styles.tableListWrapper}>
        {appVersion && (
          <IonRefresher
            slot="fixed"
            onIonRefresh={(event) => {
              onScrollEnd(event); // Pasa el evento a onScrollEnd
            }}
          >
            <IonRefresherContent
              color="danger"
              pullingIcon="bubbles"
              refreshingSpinner="bubbles"
            />
          </IonRefresher>
        )}
        <IonGrid className={styles.contentTableWrapper} data-cols={dashboardColumns.length}>
          {data.length > 0 && data?.map((rowData: Dashboard, index: number) => (
            <IonRow
              className={styles.generalTableRowData}
              key={index}
              onClick={() => handleClick({
                application: rowData.application,
                merchant_id: rowData.merchant_id,
                product: rowData.product,
                business_status: rowData.business_status,
                order: rowData.order,
                proposal: rowData.proposal,
                loan: rowData.loan,
                customer: rowData.customer,
                created_date: rowData.created_date,
              })}
            >
              {dashboardColumns.map((column) => renderColumnData(column, rowData))}
            </IonRow>
          ))}
          {data.length === 0 && !loadingData && !isRefreshing && (
            <div className={styles.noDataWrapper}>
              <div className={styles.noDataTitle}>
                <h1>¡Ups!</h1>
                {emptyMessage ? (
                  <p id={emptyMessageId} role="alert">
                    {emptyMessage}
                  </p>
                ) : (
                  <p>
                    Actualmente, no hay ninguna operación
                    <br />
                    {' '}
                    que coincida con tu búsqueda
                  </p>
                )}
              </div>
              <img src={DashboardEmpty} alt="No data" />
            </div>
          )}
        </IonGrid>
      </IonList>
      <IonInfiniteScroll
        id="dashboard-scroll"
        threshold="25px"
        disabled={!moreItems}
        ref={infiniteTarget}
        onIonInfinite={onScrollEnd}
        className={styles.scrollWrapper}
      >
        {(loadingData) && (
          <IonSpinner
            color="danger"
            name="bubbles"
            className={styles.scrollSpinner}
          />
        )}
      </IonInfiniteScroll>
    </div>
  );
}
